import React from 'react';
import './CookiePolicyModal.css';

interface CookiePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CookiePolicyModal: React.FC<CookiePolicyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="cookie-modal-backdrop" onClick={handleBackdropClick}>
      <div className="cookie-modal">
        <div className="cookie-modal-header">
          <h2>🍪 Cookie Policy</h2>
          <button 
            className="cookie-modal-close" 
            onClick={onClose}
            aria-label="Close Cookie Policy"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className="cookie-modal-content">
          <div className="cookie-section">
            <p><strong>Effective Date:</strong> October 10, 2025</p>
            <p><strong>Entity:</strong> Intelyx AI</p>
            <p><strong>Website:</strong> <a href="https://www.intelyx.ai" target="_blank" rel="noopener noreferrer">https://www.intelyx.ai</a></p>
          </div>

          <div className="cookie-section">
            <h3>1. Purpose</h3>
            <p>
              This Cookie Policy explains how Intelyx AI ("we", "our", "us") uses—or, in this case, does <strong>not</strong> use—cookies or similar technologies on our website.
            </p>
            <p>
              Our goal is to be fully transparent about the information we collect.
              Currently, our website operates <strong>without cookies, trackers, or analytics scripts</strong> of any kind.
            </p>
          </div>

          <div className="cookie-section">
            <h3>2. What Are Cookies?</h3>
            <p>
              Cookies are small text files placed on your device by websites to store preferences, analyze usage, or personalize content.
              Since we do not use them, nothing is stored or read from your browser by us or third parties.
            </p>
          </div>

          <div className="cookie-section">
            <h3>3. Our Current Cookie Use</h3>
            <ul className="cookie-list">
              <li><strong>Strictly Necessary Cookies:</strong> <span className="no-cookies">❌ None</span></li>
              <li><strong>Analytics Cookies:</strong> <span className="no-cookies">❌ None</span></li>
              <li><strong>Marketing / Advertising Cookies:</strong> <span className="no-cookies">❌ None</span></li>
              <li><strong>Third-Party Cookies:</strong> <span className="no-cookies">❌ None</span></li>
            </ul>
            <p>
              Our site may still generate standard, temporary server logs (e.g., IP address, timestamp, HTTP status) purely for <strong>security and operational</strong> reasons; these logs are not stored as cookies or linked to individuals.
            </p>
          </div>

          <div className="cookie-section">
            <h3>4. Future Changes</h3>
            <p>If we introduce functional or analytics cookies in the future—such as <strong>Google Analytics 4</strong> or <strong>LinkedIn Insight Tag</strong>—we will:</p>
            <ul>
              <li>Update this Cookie Policy accordingly</li>
              <li>Display a <strong>cookie consent banner</strong> before any non-essential cookies load</li>
              <li>Provide the ability to <strong>accept, decline, or manage</strong> cookie preferences</li>
            </ul>
          </div>

          <div className="cookie-section">
            <h3>5. Managing Cookies in Your Browser</h3>
            <p>
              Even though we don't set cookies, your browser lets you control or delete them from other sites.
              Instructions for popular browsers:
            </p>
            <ul className="browser-links">
              <li><strong>Chrome:</strong> <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Clear, enable, and manage cookies in Chrome</a></li>
              <li><strong>Firefox:</strong> <a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" target="_blank" rel="noopener noreferrer">Clear cookies and site data in Firefox</a></li>
              <li><strong>Edge:</strong> <a href="https://support.microsoft.com/en-us/microsoft-edge/view-cookies-in-microsoft-edge-a7d95376-6a7a-4142-839a-3dbe3210d611" target="_blank" rel="noopener noreferrer">View, delete, and manage cookies in Microsoft Edge</a></li>
              <li><strong>Safari:</strong> <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer">Manage cookies and website data in Safari</a></li>
            </ul>
          </div>

          <div className="cookie-section">
            <h3>6. Contact Us</h3>
            <p>Questions about this Cookie Policy or our data practices?</p>
            <p>📧 <a href="mailto:privacy@intelyx.ai">privacy@intelyx.ai</a></p>
            <p>📍 Intelyx AI, Illinois USA</p>
          </div>
        </div>
      </div>
    </div>
  );
};